import type { Task, TaskId, TaskStatus } from "../../../shared/types";
import { CATEGORY_BAR_COLORS, CATEGORY_LABELS, PRIORITY_COLORS } from "../../../shared/types";
import { CircleIcon, CircleDotIcon, CircleCheckIcon, CheckSquare, ClockIcon, ZapIcon } from "../../../ui/icons";

const STATUS_ICONS: Record<TaskStatus, React.ReactNode> = {
  todo: <CircleIcon className="w-3.5 h-3.5" />,
  doing: <CircleDotIcon className="w-3.5 h-3.5" />,
  done: <CircleCheckIcon className="w-3.5 h-3.5" />,
};

const SECTION_ICONS: Record<string, React.ReactNode> = {
  "En cours": <ZapIcon className="w-3 h-3" />,
  "À faire": <ClockIcon className="w-3 h-3" />,
  "Fait": <CheckSquare className="w-3 h-3" />,
};

interface TodayTaskSectionProps {
  readonly tasks: Task[];
  readonly title: string;
  readonly onCycleStatus: (id: TaskId) => void;
}

export function TodayTaskSection({
  tasks,
  title,
  onCycleStatus,
}: TodayTaskSectionProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <h2 className="flex items-center gap-1.5 text-xs font-medium text-ink-muted uppercase tracking-wide">
        {SECTION_ICONS[title]}
        {title}
      </h2>
      <div className="flex flex-col gap-0.5">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="group relative flex items-center gap-2 pl-4 pr-3 py-2 text-sm hover:bg-surface-alt/50 rounded-lg transition-colors cursor-pointer overflow-hidden"
            onClick={() => onCycleStatus(task.id)}
            {...(task === tasks[0] ? { "data-tutorial": "task-card" } : {})}
          >
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${CATEGORY_BAR_COLORS[task.category]}`} />
            <span
              className={`shrink-0 text-xs font-semibold ${
                task.status === "doing"
                  ? "text-accent"
                  : task.status === "done"
                    ? "text-ink-faint"
                    : "text-ink-muted"
              }`}
            >
              {STATUS_ICONS[task.status]}
            </span>
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm truncate ${
                  task.status === "done"
                    ? "line-through text-ink-faint"
                    : "text-ink"
                }`}
              >
                {task.title}
              </div>
              {(task.category || task.priority || task.tags.length > 0) && (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-ink-faint">
                    {CATEGORY_LABELS[task.category]}
                  </span>
                  {task.priority && task.priority !== "normal" && (
                    <span className={`text-[10px] font-medium uppercase ${PRIORITY_COLORS[task.priority]}`}>
                      {task.priority}
                    </span>
                  )}
                  {task.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] text-ink-faint"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
